import {
  addDays,
  format,
  isSameDay,
  isToday,
  parseISO,
  startOfWeek,
} from "date-fns";

const DATE_PARAM_FORMAT = "yyyy-MM-dd";

/** Serialize a date to the `?week=YYYY-MM-DD` URL param / API range bound. */
export const toISODate = (date: Date): string =>
  format(date, DATE_PARAM_FORMAT);

/** Monday at 00:00 of the week containing `date`. */
export const mondayOf = (date: Date): Date =>
  startOfWeek(date, { weekStartsOn: 1 });

/** Resolve a `?week=` value to its date, falling back to today. */
export const parseDateParam = (value: string | undefined): Date => {
  if (!value) return new Date();
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

/** `count` consecutive days starting at `anchor`. */
export const rangeDays = (anchor: Date, count: number): Date[] =>
  Array.from({ length: count }, (_, i) => addDays(anchor, i));

/** Shift a date forward/back by whole days. */
export const shiftDays = (date: Date, delta: number): Date =>
  addDays(date, delta);

export const isCurrentDay = (date: Date): boolean => isToday(date);

export const formatWeekday = (date: Date): string => format(date, "EEE");

export const formatDayNumber = (date: Date): string => format(date, "d");

/** Human label for the visible range — a single day, or a start–end span. */
export const formatRange = (start: Date, end: Date): string => {
  if (isSameDay(start, end)) return format(start, "EEE, MMM d, yyyy");
  const sameMonth = format(start, "MMM yyyy") === format(end, "MMM yyyy");
  return sameMonth
    ? `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`
    : `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
};
